// ============================================
// robson.jsx  —  Assistente flutuante do ZipHub (renderiza HTML)
// ============================================
// Monte UMA vez, dentro do <BrowserRouter> e FORA do <Routes>:
//   <Robson conta={localStorage.idconta} apiUrl="https://node.ziphub.com.br" esconderEm={['/login']} />
// ============================================

import { useState, useRef, useEffect, Fragment } from 'react'
import { useLocation } from 'react-router-dom'

export default function Robson({ conta, apiUrl = '', esconderEm = ['/login'] }) {
  const location = useLocation()
  const rotaAtual = location.pathname.toLowerCase()
  const deveEsconder = esconderEm.some((p) => rotaAtual === p.toLowerCase())

  const [aberto, setAberto] = useState(false)
  const [expandido, setExpandido] = useState(false)  // modo modal central
  const [pergunta, setPergunta] = useState('')
  const [carregando, setCarregando] = useState(false)
  // guarda a ação de ML aguardando confirmação (preview já feito)
  const [acaoPendente, setAcaoPendente] = useState(null)
  // diálogo aguardando uma resposta digitada (ex: quantidade livre)
  const [aguardando, setAguardando] = useState(null)
  const [mensagens, setMensagens] = useState([
    {
      de: 'bot',
      html: 'Oi, eu sou o <b>Robson</b>! 👋 Posso consultar seus dados. Ex: <i>"qual o último pedido?"</i>'
    }
  ])

  const fimRef = useRef(null)
  const inputRef = useRef(null)

  useEffect(() => {
    fimRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [mensagens, aberto])

  useEffect(() => {
    if (aberto) inputRef.current?.focus()
  }, [aberto])

  // helpers pra adicionar mensagens
  const addBot = (html) => setMensagens((m) => [...m, { de: 'bot', html }])
  // mensagem do bot com botões clicáveis: botoes = [{ label, onClick }]
  const addBotBotoes = (html, botoes) =>
    setMensagens((m) => [...m, { de: 'bot', html, botoes }])

  // ---- passos (steps) que aparecem como card no chat ----
  // cria um card e retorna funções pra atualizar os passos ao vivo
  const criarPassos = (titulo, labels) => {
    const id = 'steps_' + Date.now() + '_' + Math.random().toString(36).slice(2, 6)
    const passos = labels.map((l) => ({ label: l, status: 'wait' })) // wait|run|ok|err
    setMensagens((m) => [...m, { de: 'bot', steps: { id, titulo, passos } }])
    const atualizar = (idx, status, extra, link) => {
      setMensagens((m) => m.map((msg) => {
        if (msg.steps?.id !== id) return msg
        const novos = msg.steps.passos.map((p, i) =>
          i === idx ? { ...p, status, extra: extra ?? p.extra, link: link ?? p.link } : p)
        return { ...msg, steps: { ...msg.steps, passos: novos } }
      }))
    }
    return {
      run: (i, extra, link) => atualizar(i, 'run', extra, link),
      ok: (i, extra, link) => atualizar(i, 'ok', extra, link),
      err: (i, extra, link) => atualizar(i, 'err', extra, link),
    }
  }
  const iduser = () => (typeof localStorage !== 'undefined' ? localStorage.idusuario : null)

  async function enviar(textoDireto) {
    const texto = (typeof textoDireto === 'string' ? textoDireto : pergunta).trim()
    if (!texto || carregando) return

    setMensagens((m) => [...m, { de: 'user', texto }])
    setPergunta('')
    setCarregando(true)

    try {
      // aguardando uma quantidade digitada (fluxo "Outro valor…")
      if (aguardando?.tipo === 'qtd_variacao') {
        const q = parseInt(texto.replace(/\D/g, ''), 10)
        const ctx = aguardando
        setAguardando(null)
        if (isNaN(q) || q <= 0) {
          addBot('<p>Quantidade inválida. Digite só um número, ex: 15.</p>')
        } else {
          pedirConfirmacaoAtivar(ctx.sku, ctx.achados, q)
        }
        setCarregando(false)
        return
      }

      if (aguardando?.tipo === 'qtd_revisao') {
        const q = parseInt(texto.replace(/\D/g, ''), 10)
        const ctx = aguardando
        setAguardando(null)
        if (isNaN(q) || q <= 0) addBot('<p>Quantidade inválida.</p>')
        else executarRevisao({ ...ctx.plano, _qtd: q })
        setCarregando(false)
        return
      }

      if (aguardando?.tipo === 'qtd_ativar_tudo') {
        const q = parseInt(texto.replace(/\D/g, ''), 10)
        const ctx = aguardando
        setAguardando(null)
        if (isNaN(q) || q <= 0) {
          addBot('<p>Quantidade inválida. Digite só um número, ex: 15.</p>')
        } else {
          executarTudo('ativar', ctx.dados, q)
        }
        setCarregando(false)
        return
      }

      // ── Se há uma ação aguardando confirmação, interpreta sim/não ──
      if (acaoPendente) {
        const t = texto.toLowerCase()
        const sim = ['sim', 's', 'confirmar', 'confirmo', 'pode', 'ok'].includes(t)
        const nao = ['não', 'nao', 'n', 'cancelar', 'cancela'].includes(t)

        if (nao) {
          setAcaoPendente(null)
          addBot('<p>Ok, cancelei a ação. 👍</p>')
          setCarregando(false)
          return
        }
        if (sim) {
          const resp = await fetch(`${apiUrl}/api/ia/acao/executar/${conta}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ pendente: acaoPendente, iduser: iduser() })
          })
          const data = await resp.json()
          setAcaoPendente(null)
          addBot(data.html || `<p>${data.error || 'Ação concluída.'}</p>`)
          setCarregando(false)
          return
        }
        // resposta ambígua: continua aguardando
        addBot('<p>Responda <b>Sim</b> para executar ou <b>Não</b> para cancelar.</p>')
        setCarregando(false)
        return
      }

      // ── Fluxo normal: manda pro chat (que decide se é SQL, geral ou ação) ──
      const resp = await fetch(`${apiUrl}/api/ia/chat/${conta}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          pergunta: texto,
          iduser: iduser(),
          historico: mensagens.filter((m) => m.texto || m.html).map((m) => ({
            de: m.de,
            texto: m.texto || (m.html ? m.html.replace(/<[^>]+>/g, ' ').trim() : '')
          }))
        })
      })
      const data = await resp.json()

      // É ação em VARIAÇÃO? Inicia o diálogo conversacional com botões.
      if (data.tipo === 'acao_variacao') {
        await iniciarDialogoVariacao(data.acao, data.sku, data.qtd)
        setCarregando(false)
        return
      }

      // Relatório de itens vendidos por conta
      if (data.tipo === 'vendidos') {
        const st = criarPassos(`📊 Itens vendidos (últimos ${data.dias} dias)`, [
          'Consultando o banco de dados',
          'Agrupando por conta',
        ])
        st.run(0)
        try {
          const r = await fetch(`${apiUrl}/api/ia/acao/vendidos/${conta}`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ dias: data.dias })
          })
          const d = await r.json()
          if (d.itens && d.itens.length) {
            st.ok(0, `${d.itens.length} itens`)
            st.ok(1, 'pronto')
            setMensagens((m) => [...m, { de: 'bot', vendidos: d.itens, dias: data.dias }])
            setExpandido(true)
          } else {
            st.err(0, 'sem dados')
            addBot(`<p>${d.error || 'Nenhum item vendido no período.'}</p>`)
          }
        } catch {
          st.err(0, 'erro')
          addBot('<p>Erro ao buscar os itens vendidos.</p>')
        }
        setCarregando(false)
        return
      }

      // É ação de ML? Então faz o PREVIEW (não executa ainda)
      if (data.tipo === 'acao_ml') {
        // ativar/pausar; se veio quantidade no ativar, guarda pra aplicar
        await buscarTudoENarrar(data.acao, data.sku, data.qtd)
        setCarregando(false)
        return
      }

      // alterar saldo: busca tudo (ativos e pausados) e muda a quantidade
      if (data.tipo === 'alterar_saldo') {
        await buscarTudoENarrar('saldo', data.sku, data.qtd)
        setCarregando(false)
        return
      }

      // É consulta de anúncios? Renderiza o PAINEL interativo (editar em lote)
      if (data.tipo === 'consulta_anuncios') {
        addBot(data.resposta)
        const r = await fetch(`${apiUrl}/api/ia/acao/anuncios/${conta}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(data.porMlb ? { porMlb: data.porMlb } : { sku: data.sku })
        })
        const ad = await r.json()
        if (ad.anuncios && ad.anuncios.length) {
          // mensagem especial: painel interativo
          setMensagens((m) => [...m, { de: 'bot', painel: ad.anuncios, sku: ad.sku || data.sku || data.porMlb }])
          setExpandido(true) // painel de anúncios abre em tela cheia
        } else {
          addBot(ad.html || `<p>${ad.error || 'Não encontrei anúncios.'}</p>`)
        }
        setCarregando(false)
        return
      }

      // Resposta normal (SQL/geral)
      addBot(data.resposta || `<p>${data.error || 'Sem resposta.'}</p>`)
    } catch (e) {
      addBot('<p>Ops, não consegui falar com o servidor.</p>')
    } finally {
      setCarregando(false)
    }
  }

  // abre o painel completo de anúncios de um SKU (a partir da lista de vendidos)
  async function abrirAnunciosSku(sku) {
    addBot(`<p>Abrindo os anúncios do SKU <b>${sku}</b>…</p>`)
    try {
      const r = await fetch(`${apiUrl}/api/ia/acao/anuncios/${conta}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sku })
      })
      const ad = await r.json()
      if (ad.anuncios && ad.anuncios.length) {
        setMensagens((m) => [...m, { de: 'bot', painel: ad.anuncios, sku: ad.sku || sku }])
        setExpandido(true)
      } else {
        addBot(ad.html || `<p>Nenhum anúncio encontrado para ${sku}.</p>`)
      }
    } catch {
      addBot('<p>Erro ao abrir os anúncios.</p>')
    }
  }

  // ============================================
  // Diálogo conversacional de variação (com botões)
  // ============================================
  async function iniciarDialogoVariacao(acao, sku, qtdJa) {
    const st = criarPassos(`${acao === 'pausar' ? '🔴 Pausar' : '🟢 Ativar'} variação ${sku}`, [
      'Procurando a variação nos anúncios',
      'Variação localizada',
    ])
    st.run(0)
    let achados = []
    try {
      const r = await fetch(`${apiUrl}/api/ia/acao/localizar-variacao/${conta}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sku })
      })
      achados = (await r.json()).achados || []
    } catch { /* ignora */ }

    if (!achados.length) {
      st.err(0, 'não encontrada')
      addBot(`<p>Não achei a variação <b>${sku}</b> em nenhum anúncio. Confere o SKU.</p>`)
      return
    }
    st.ok(0, `${achados.length} anúncio(s)`)
    st.ok(1, achados.map((a) => a.combinacao || a.mlb).join(', '))

    const ondeTxt = achados.map((a) => `${a.combinacao || 'variação'} · ${a.mlb} (${a.conta})`).join('<br>')

    if (acao === 'pausar') {
      addBotBotoes(
        `<p>Achei a variação <b>${sku}</b> em <b>${achados.length}</b> anúncio(s):</p>` +
        `<p><small>${ondeTxt}</small></p>` +
        `<p>Confirma <b>pausar</b> (zerar estoque) essa variação?</p>`,
        [
          { label: '✅ Sim, pausar', onClick: () => executarVariacao(sku, achados, 0) },
          { label: '❌ Cancelar', onClick: () => addBot('<p>Ok, cancelei. 👍</p>') },
        ]
      )
      return
    }

    // ativar: se já veio quantidade na frase, confirma direto; senão pergunta
    if (qtdJa && qtdJa > 0) {
      pedirConfirmacaoAtivar(sku, achados, qtdJa)
    } else {
      addBotBotoes(
        `<p>Achei a variação <b>${sku}</b> em <b>${achados.length}</b> anúncio(s).</p>` +
        `<p>Com <b>quantas unidades</b> quer ativar?</p>`,
        [
          { label: '5', onClick: () => pedirConfirmacaoAtivar(sku, achados, 5) },
          { label: '10', onClick: () => pedirConfirmacaoAtivar(sku, achados, 10) },
          { label: '20', onClick: () => pedirConfirmacaoAtivar(sku, achados, 20) },
          { label: '50', onClick: () => pedirConfirmacaoAtivar(sku, achados, 50) },
          { label: 'Outro valor…', onClick: () => perguntarQtdLivre(sku, achados) },
        ]
      )
    }
  }

  function perguntarQtdLivre(sku, achados) {
    // ativa o "modo aguardando quantidade": a próxima mensagem digitada vira a qtd
    setAguardando({ tipo: 'qtd_variacao', sku, achados })
    addBot('<p>Digite a quantidade que deseja (só o número).</p>')
  }

  function pedirConfirmacaoAtivar(sku, achados, qtd) {
    addBotBotoes(
      `<p>Confirma <b>ativar</b> a variação <b>${sku}</b> com <b>${qtd}</b> unidade(s)?</p>`,
      [
        { label: `✅ Sim, ativar (${qtd})`, onClick: () => executarVariacao(sku, achados, qtd) },
        { label: '❌ Cancelar', onClick: () => addBot('<p>Ok, cancelei. 👍</p>') },
      ]
    )
  }

  async function executarVariacao(sku, achados, quantidade) {
    const st = criarPassos(
      `${quantidade > 0 ? '🟢 Ativando' : '🔴 Pausando'} variação ${sku}`,
      achados.map((a) => `${a.combinacao || 'variação'} · ${a.mlb} (${a.conta})`)
    )
    for (let i = 0; i < achados.length; i++) {
      const a = achados[i]
      st.run(i)
      try {
        const r = await fetch(`${apiUrl}/api/ia/acao/variacoes/salvar/${conta}`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ mlb: a.mlb, seller: a.seller, sku, mudancas: { [a.idVar]: { quantity: quantidade } }, iduser: iduser() })
        })
        const d = await r.json()
        if (d.ok) st.ok(i, quantidade > 0 ? `ativada (${quantidade})` : 'pausada')
        else st.err(i, d.erro || 'erro')
      } catch {
        st.err(i, 'erro de conexão')
      }
    }

    // se PAUSOU (quantidade 0), verifica se o SKU está em kits e pergunta
    if (quantidade === 0) {
      await verificarKitsEPerguntar(sku)
    }
  }

  // pede uma frase de narração à IA (com fallback)
  async function narrar(contexto, fallback) {
    try {
      const r = await fetch(`${apiUrl}/api/ia/acao/narrar/${conta}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ contexto })
      })
      const d = await r.json()
      return d.frase || fallback
    } catch {
      return fallback
    }
  }

  // busca profunda (anúncios + variações + kits), narrando com IA
  // acao: 'pausar' | 'ativar' | 'saldo'  (saldo usa novoSaldo pra mudar a qtd)
  async function buscarTudoENarrar(acao, sku, novoSaldo) {
    const verbo = acao === 'pausar' ? '🔴 Pausar'
                : acao === 'ativar' ? '🟢 Ativar'
                : `📦 Alterar saldo p/ ${novoSaldo}`
    const st = criarPassos(`${verbo} — SKU ${sku}`, [
      'Procurando anúncios diretos…',
      'Procurando variações…',
      'Procurando kits…',
    ])
    st.run(0); st.run(1); st.run(2)

    // inicia o JOB (retorna na hora) e fica consultando o progresso (polling)
    let dados = null
    try {
      const rInit = await fetch(`${apiUrl}/api/ia/acao/buscar-tudo/${conta}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sku, acao })
      })
      const { jobId, error } = await rInit.json()
      if (!jobId) throw new Error(error || 'sem job')

      // consulta o status a cada 2s. Só desiste se o servidor disser 'erro'
      // de verdade, ou se ficar MUITO tempo sem o job avançar.
      const espera = (ms) => new Promise((res) => setTimeout(res, ms))
      let tentativas = 0
      let ultimoErro = ''
      let semAvanco = 0        // ciclos sem mudança de progresso
      let assinaturaAnterior = ''
      const MAX_TENTATIVAS = 1200  // ~40 min de teto (pode ter centenas de kits)
      const MAX_SEM_AVANCO = 120   // ~4 min travado no mesmo ponto = desiste

      while (tentativas < MAX_TENTATIVAS) {
        await espera(2000)
        tentativas++
        let j = null
        try {
          const rj = await fetch(`${apiUrl}/api/ia/acao/job/${jobId}`)
          if (rj.status === 404) {
            // job foi limpo/sumiu no servidor: não desiste na hora, conta como sem-avanço
            semAvanco++
            if (semAvanco >= MAX_SEM_AVANCO) { ultimoErro = 'o processo sumiu no servidor'; break }
            continue
          }
          j = await rj.json()
        } catch {
          // falha de rede momentânea: NÃO desiste, tenta de novo
          continue
        }

        const p = j.progresso || {}
        if (p.anuncios !== undefined && p.anuncios !== 'rodando') st.ok(0, `${p.anuncios} anúncio(s)`)
        else st.run(0, j.etapa || 'procurando…')
        if (p.variacoes !== undefined && p.variacoes !== 'rodando') st.ok(1, `${p.variacoes} variação(ões)`)
        // kits: pode vir "rodando", "45/200" (progresso) ou número final
        if (p.kits !== undefined && p.kits !== 'rodando' && !String(p.kits).includes('/')) {
          st.ok(2, `${p.kits} kit(s)`)
        } else if (String(p.kits).includes('/')) {
          st.run(2, `analisando ${p.kits}`)
        }

        // detecta se está avançando (pra não ficar preso pra sempre)
        const assinatura = JSON.stringify(p) + '|' + (j.etapa || '')
        if (assinatura === assinaturaAnterior) semAvanco++
        else { semAvanco = 0; assinaturaAnterior = assinatura }

        if (j.status === 'concluido') { dados = j.resultado; break }
        if (j.status === 'erro') { ultimoErro = j.erro || 'erro no servidor'; break }
        if (semAvanco >= MAX_SEM_AVANCO) { ultimoErro = 'o processo travou (sem avanço por ~3 min)'; break }
      }
      if (!dados && !ultimoErro) ultimoErro = 'demorou demais'
      if (ultimoErro) { st.err(2, ultimoErro) }
    } catch (err) {
      dados = null
    }

    if (!dados || dados.error) {
      st.err(0, 'erro'); st.err(1, 'erro'); st.err(2, 'erro')
      addBot('<p>Deu erro na busca. Tenta de novo.</p>')
      return
    }

    const nDiretos = (dados.anunciosDiretos || []).length
    const nVar = (dados.variacoes || []).length
    const nKits = (dados.kits || []).length

    // marca os passos com o resultado e pede narração da IA (sem travar)
    st.ok(0, `${nDiretos} anúncio(s)`)
    st.ok(1, `${nVar} variação(ões)`)
    st.ok(2, `${nKits} kit(s)`)

    // narração informal da IA como uma mensagem à parte (não bloqueia)
    narrar(
      `Terminei de procurar o SKU ${sku}: achei ${nDiretos} anúncios, ${nVar} variações e ${nKits} kits`,
      ''
    ).then((frase) => { if (frase) addBot(`<p>${frase}</p>`) })

    const totalKitItens = (dados.kits || []).length
    const total = nDiretos + nVar + totalKitItens

    if (total === 0) {
      const jaTxt = acao === 'ativar' ? 'Todos já estão ativos'
                  : acao === 'pausar' ? 'Todos já estão pausados' : 'Nada encontrado'
      addBot(`<p>Nada pra fazer no SKU <b>${sku}</b>. ${jaTxt}.</p>`)
      return
    }

    // narração informal (não bloqueia)
    narrar(
      `Terminei de procurar o SKU ${sku}: achei ${nDiretos} anúncios, ${nVar} variações e ${totalKitItens} em kits`,
      ''
    ).then((frase) => { if (frase) addBot(`<p>${frase}</p>`) })

    // guarda o novoSaldo dentro dos dados (pra ação 'saldo')
    if (acao === 'saldo') dados.novoSaldo = novoSaldo
    // no ativar, se veio quantidade, guarda pra usar na execução
    if (acao === 'ativar' && novoSaldo !== undefined) dados.qtdAtivar = novoSaldo

    // mostra o card de REVISÃO com checkboxes (o usuário escolhe o que fazer)
    setMensagens((m) => [...m, { de: 'bot', revisao: dados }])
  }

  // ao ATIVAR: itens com estoque ativam direto; sem estoque pergunta a qtd (uma vez)
  function prepararAtivarTudo(dados) {
    // detecta itens sem estoque
    const semEstoqueDiretos = (dados.anunciosDiretos || []).filter((a) => (Number(a.qtd) || 0) <= 0)
    const semEstoqueVar = (dados.variacoes || []).filter((v) => (Number(v.quantidade) || 0) <= 0)
    // kits: anúncios sem estoque
    let semEstoqueKit = 0
    ;(dados.kits || []).forEach((k) => (k.anuncios || []).forEach((a) => { if ((Number(a.qtd) || 0) <= 0) semEstoqueKit++ }))
    const totalSemEstoque = semEstoqueDiretos.length + semEstoqueVar.length + semEstoqueKit

    if (totalSemEstoque === 0) {
      // todos têm estoque: ativa direto
      executarTudo('ativar', dados, null)
      return
    }

    // pergunta a quantidade UMA vez pros sem estoque
    addBotBotoes(
      `<p><b>${totalSemEstoque} item(ns) estão sem estoque.</b> Pra ativar, precisa de quantidade.</p>` +
      `<p>Com quantas unidades quer ativar os que estão zerados? (os que já têm estoque ativam direto)</p>`,
      [
        { label: '5', onClick: () => executarTudo('ativar', dados, 5) },
        { label: '10', onClick: () => executarTudo('ativar', dados, 10) },
        { label: '20', onClick: () => executarTudo('ativar', dados, 20) },
        { label: '50', onClick: () => executarTudo('ativar', dados, 50) },
        { label: 'Outro valor…', onClick: () => {
            setAguardando({ tipo: 'qtd_ativar_tudo', dados })
            addBot('<p>Digite a quantidade (só o número).</p>')
          } },
      ]
    )
  }

  // executa o plano escolhido no card de revisão
  async function executarRevisao(plano) {
    const { acao, sku } = plano
    const ehSaldo = acao === 'saldo'
    const status = acao === 'pausar' ? 'paused' : 'active'
    const novoSaldo = plano.novoSaldo  // usado quando acao === 'saldo'

    // proteção: no modo saldo, o valor precisa ser um número válido
    if (ehSaldo && (novoSaldo === undefined || novoSaldo === null || isNaN(Number(novoSaldo)))) {
      addBot('<p>❌ Não entendi o valor do saldo. Tenta assim: <b>alterar saldo 508375 pra 10</b></p>')
      return
    }

    if (acao === 'ativar') {
      const semEstoque =
        plano.anuncios.filter((a) => (Number(a.qtd) || 0) <= 0).length +
        plano.variacoes.filter((v) => (Number(v.quantidade) || 0) <= 0).length
      if (semEstoque > 0 && !plano._qtd) {
        addBotBotoes(
          `<p><b>${semEstoque} item(ns) sem estoque.</b> Com quantas unidades ativar?</p>`,
          [5, 10, 20, 50].map((q) => ({ label: String(q), onClick: () => executarRevisao({ ...plano, _qtd: q }) }))
            .concat([{ label: 'Outro…', onClick: () => { setAguardando({ tipo: 'qtd_revisao', plano }); addBot('<p>Digite a quantidade.</p>') } }])
        )
        return
      }
    }
    const qtdAtivar = plano._qtd || 1

    const labels = []
    plano.anuncios.forEach((a) => labels.push(`📦 ${a.mlb} (${a.conta})`))
    plano.variacoes.forEach((v) => labels.push(`🎨 ${v.combinacao || v.mlb}`))
    plano.kits.forEach((k) => labels.push(`🧩 ${k.kitSku} ${k.acao_kit === 'anuncio' ? '(anúncio)' : '(variação)'}`))

    const titulo = ehSaldo ? `📦 Saldo → ${novoSaldo} — SKU ${sku}`
                 : `${acao === 'pausar' ? '🔴 Pausando' : '🟢 Ativando'} SKU ${sku}`
    const st = criarPassos(titulo, labels)
    let idx = 0

    // 1) anúncios diretos
    for (const a of plano.anuncios) {
      st.run(idx)
      try {
        const alt = { seller: a.seller, mlb: a.mlb, sku: a.sku, origem: a.origem }
        if (ehSaldo) {
          alt.quantity = Number(novoSaldo)   // só muda o saldo, sem mexer status
        } else {
          alt.status = status
          if (acao === 'ativar' && (Number(a.qtd) || 0) <= 0) alt.quantity = qtdAtivar
        }
        const r = await fetch(`${apiUrl}/api/ia/acao/editar/${conta}`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ alteracoes: [alt], iduser: iduser() })
        })
        const d = await r.json()
        d.ok ? st.ok(idx, ehSaldo ? `saldo ${novoSaldo}` : 'ok', a.link) : st.err(idx, 'erro', a.link)
      } catch { st.err(idx, 'erro') }
      idx++
    }

    // 2) variações
    for (const v of plano.variacoes) {
      st.run(idx)
      try {
        const q = ehSaldo ? Number(novoSaldo)
                : acao === 'pausar' ? 0
                : ((Number(v.quantidade) || 0) > 0 ? Number(v.quantidade) : qtdAtivar)
        const r = await fetch(`${apiUrl}/api/ia/acao/variacoes/salvar/${conta}`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ mlb: v.mlb, seller: v.seller, sku, mudancas: { [v.idVar]: { quantity: q } }, iduser: iduser() })
        })
        const d = await r.json()
        d.ok ? st.ok(idx, ehSaldo ? `saldo ${q}` : (acao === 'pausar' ? 'pausada' : `ativada (${q})`), v.link) : st.err(idx, d.erro || 'erro')
      } catch { st.err(idx, 'erro') }
      idx++
    }

    // 3) kits
    for (const k of plano.kits) {
      st.run(idx)
      try {
        const linkKit = `https://produto.mercadolivre.com.br/${k.mlb}`
        if (k.acao_kit === 'anuncio' && !ehSaldo) {
          const alt = { seller: k.seller, mlb: k.mlb, sku: k.kitSku, origem: 'seller_sku', status }
          const r = await fetch(`${apiUrl}/api/ia/acao/editar/${conta}`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ alteracoes: [alt], iduser: iduser() })
          })
          const d = await r.json()
          d.ok ? st.ok(idx, `kit inteiro (${k.motivo})`, linkKit) : st.err(idx, 'erro', linkKit)
        } else {
          // saldo ou variação: muda a quantidade da variação do kit
          const q = ehSaldo ? Number(novoSaldo) : (acao === 'pausar' ? 0 : qtdAtivar)
          const r = await fetch(`${apiUrl}/api/ia/acao/variacoes/salvar/${conta}`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ mlb: k.mlb, seller: k.seller, sku: k.kitSku, mudancas: { [k.idVar]: { quantity: q } }, iduser: iduser() })
          })
          const d = await r.json()
          d.ok ? st.ok(idx, ehSaldo ? `saldo ${q}` : `só a variação ${k.combinacao || ''}`, linkKit) : st.err(idx, d.erro || 'erro', linkKit)
        }
      } catch { st.err(idx, 'erro') }
      idx++
    }

    addBot(`<p><b>Concluído!</b> Clique nos 🔗 pra conferir no ML.</p>`)
  }

  // executa a ação em tudo. qtdAtivar = quantidade pra itens sem estoque (só no ativar)
  async function executarTudo(acao, dados, qtdAtivar) {
    const status = acao === 'pausar' ? 'paused' : 'active'

    const labels = []
    ;(dados.anunciosDiretos || []).forEach((a) => labels.push(`Anúncio ${a.mlb} (${a.conta})`))
    ;(dados.variacoes || []).forEach((v) => labels.push(`Variação ${v.combinacao || v.mlb}`))
    ;(dados.kits || []).forEach((k) => (k.anuncios || []).forEach((a) => labels.push(`Kit ${k.sku} · ${a.mlb}`)))

    const st = criarPassos(`${acao === 'pausar' ? '🔴 Pausando' : '🟢 Ativando'} SKU ${dados.sku}`, labels)
    let idx = 0

    // qtd pra usar num item: se ativar e sem estoque, usa qtdAtivar; senão mantém
    const qtdPara = (qtdAtual) => {
      if (acao !== 'ativar') return undefined
      const q = Number(qtdAtual) || 0
      return q > 0 ? undefined : (qtdAtivar || 1) // com estoque: não mexe; sem: repõe
    }

    // 1) anúncios diretos
    for (const a of (dados.anunciosDiretos || [])) {
      st.run(idx)
      try {
        const alt = { seller: a.seller, mlb: a.mlb, sku: a.sku, origem: a.origem, status }
        const q = qtdPara(a.qtd)
        if (q !== undefined) alt.quantity = q
        const r = await fetch(`${apiUrl}/api/ia/acao/editar/${conta}`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ alteracoes: [alt], iduser: iduser() })
        })
        const d = await r.json()
        d.ok ? st.ok(idx, q !== undefined ? `ok (qtd ${q})` : 'ok', a.link) : st.err(idx, 'erro', a.link)
      } catch { st.err(idx, 'erro') }
      idx++
    }

    // 2) variações
    for (const v of (dados.variacoes || [])) {
      st.run(idx)
      try {
        const q = acao === 'pausar' ? 0 : ((Number(v.quantidade) || 0) > 0 ? Number(v.quantidade) : (qtdAtivar || 1))
        const r = await fetch(`${apiUrl}/api/ia/acao/variacoes/salvar/${conta}`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ mlb: v.mlb, seller: v.seller, sku: dados.sku, mudancas: { [v.idVar]: { quantity: q } }, iduser: iduser() })
        })
        const d = await r.json()
        d.ok ? st.ok(idx, acao === 'pausar' ? 'pausada' : `ativada (${q})`) : st.err(idx, d.erro || 'erro')
      } catch { st.err(idx, 'erro') }
      idx++
    }

    // 3) kits
    for (const k of (dados.kits || [])) {
      for (const a of (k.anuncios || [])) {
        st.run(idx)
        try {
          const alt = { seller: a.seller, mlb: a.mlb, sku: a.sku, origem: a.origem, status }
          const q = qtdPara(a.qtd)
          if (q !== undefined) alt.quantity = q
          const r = await fetch(`${apiUrl}/api/ia/acao/editar/${conta}`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ alteracoes: [alt], iduser: iduser() })
          })
          const d = await r.json()
          d.ok ? st.ok(idx, 'ok') : st.err(idx, 'erro')
        } catch { st.err(idx, 'erro') }
        idx++
      }
    }

    addBot(`<p><b>Concluído!</b> Ação aplicada no SKU ${dados.sku}.</p>`)
  }

  // procura kits que usam o SKU e pergunta se quer pausar também
  async function verificarKitsEPerguntar(sku) {
    try {
      const r = await fetch(`${apiUrl}/api/ia/acao/kits-do-sku/${conta}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sku })
      })
      const kits = (await r.json()).kits || []
      if (!kits.length) return

      const lista = kits.map((k) => `${k.sku}${k.descricao ? ' — ' + k.descricao : ''}`).join('<br>')
      addBotBotoes(
        `<p>⚠️ O SKU <b>${sku}</b> faz parte de <b>${kits.length}</b> kit(s):</p>` +
        `<p><small>${lista}</small></p>` +
        `<p>Quer <b>pausar esses kits</b> também? (eles vendem um item que ficou sem estoque)</p>`,
        [
          { label: `🔴 Sim, pausar ${kits.length} kit(s)`, onClick: () => pausarKits(kits) },
          { label: '❌ Não, deixar os kits', onClick: () => addBot('<p>Ok, deixei os kits como estavam.</p>') },
        ]
      )
    } catch { /* silencioso */ }
  }

  // pausa os anúncios de cada kit
  async function pausarKits(kits) {
    const st = criarPassos('🔴 Pausando kits', kits.map((k) => `${k.sku}${k.descricao ? ' — ' + k.descricao.slice(0, 30) : ''}`))
    for (let i = 0; i < kits.length; i++) {
      st.run(i)
      try {
        // busca os anúncios do kit e pausa todos
        const rb = await fetch(`${apiUrl}/api/ia/acao/anuncios/${conta}`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ sku: kits[i].sku })
        })
        const ad = await rb.json()
        const anuncios = (ad.anuncios || []).filter((a) => String(a.status).toLowerCase() === 'active')
        if (!anuncios.length) { st.ok(i, 'nenhum anúncio ativo'); continue }
        const alteracoes = anuncios.map((a) => ({ seller: a.seller, mlb: a.mlb, sku: a.sku, origem: a.origem, status: 'paused' }))
        const rp = await fetch(`${apiUrl}/api/ia/acao/editar/${conta}`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ alteracoes, iduser: iduser() })
        })
        const dp = await rp.json()
        st.ok(i, `${dp.ok || 0} anúncio(s) pausado(s)`)
      } catch {
        st.err(i, 'erro')
      }
    }
  }

  function onKey(e) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      enviar()
    }
  }

  if (deveEsconder) return null

  return (
    <>
      {/* CSS pra deixar o HTML das respostas bonito (tabelas, listas, etc) */}
      <style>{cssRespostas}</style>

      <button
        onClick={() => setAberto((v) => !v)}
        className="robson-botao"
        aria-label="Abrir assistente Robson"
        title="Falar com o Robson"
      >
        {aberto ? '✕' : '🤖'}
      </button>

      {aberto && (
        <>
          {/* fundo escuro só no modo expandido; clicar fora fecha o modal */}
          {expandido && (
            <div className="robson-backdrop" onClick={() => setExpandido(false)} />
          )}

          <div className={`robson-janela ${expandido ? 'robson-expandido' : ''}`}>
            <div style={estilos.header} className="robson-header">
              <span style={estilos.avatar}>🤖</span>
              <div style={{ flex: 1 }}>
                <div style={estilos.nome}>Robson</div>
                <div style={estilos.sub}>Assistente ZipHub</div>
              </div>
              {/* botão expandir / recolher */}
              <button
                onClick={() => setExpandido((v) => !v)}
                style={estilos.iconBtn}
                title={expandido ? 'Recolher' : 'Expandir'}
                aria-label={expandido ? 'Recolher' : 'Expandir'}
              >
                {expandido ? '🗗' : '🗖'}
              </button>
              {/* botão fechar */}
              <button
                onClick={() => { setAberto(false); setExpandido(false) }}
                style={estilos.iconBtn}
                title="Fechar"
                aria-label="Fechar"
              >
                ✕
              </button>
            </div>

            <div style={estilos.corpo} className="robson-corpo">
            {mensagens.map((msg, i) =>
              msg.revisao ? (
                <CardRevisao key={i} dados={msg.revisao} conta={conta} apiUrl={apiUrl}
                  iduser={iduser()} onExecutar={(plano) => executarRevisao(plano)} />
              ) : msg.vendidos ? (
                <PainelVendidos key={i} itens={msg.vendidos} dias={msg.dias}
                  conta={conta} apiUrl={apiUrl}
                  onAbrirSku={(sku) => abrirAnunciosSku(sku)} />
              ) : msg.steps ? (
                <div key={i} className="steps-card">
                  <div className="steps-titulo">{msg.steps.titulo}</div>
                  {msg.steps.passos.map((p, pi) => (
                    <div key={pi} className={`step-linha step-${p.status}`}>
                      <span className="step-icone">
                        {p.status === 'ok' ? '✅' : p.status === 'err' ? '❌' : p.status === 'run' ? '⏳' : '⚪'}
                      </span>
                      <span className="step-label">
                        {p.label}{p.extra ? ` — ${p.extra}` : ''}
                        {p.link && (
                          <a href={p.link} target="_blank" rel="noopener noreferrer" className="step-link">🔗 ver no ML</a>
                        )}
                      </span>
                    </div>
                  ))}
                </div>
              ) : msg.painel ? (
                <PainelAnuncios
                  key={i}
                  anuncios={msg.painel}
                  sku={msg.sku}
                  conta={conta}
                  apiUrl={apiUrl}
                  iduser={iduser()}
                  onResultado={(html) => addBot(html)}
                />
              ) : msg.de === 'user' ? (
                <div key={i} className="robson-balao" style={{ ...estilos.balao, ...estilos.balaoUser }}>
                  {msg.texto}
                </div>
              ) : (
                <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', maxWidth: '90%' }}>
                  <div
                    className="robson-balao robson-html"
                    style={{ ...estilos.balao, ...estilos.balaoBot }}
                    dangerouslySetInnerHTML={{ __html: msg.html }}
                  />
                  {msg.botoes && (
                    <div className="robson-botoes">
                      {msg.botoes.map((b, bi) => (
                        <button key={bi} type="button" className="robson-botao-acao"
                          onClick={() => b.onClick()}>
                          {b.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )
            )}

            {carregando && (
              <div style={{ ...estilos.balao, ...estilos.balaoBot, ...estilos.digitando }}>
                Robson está consultando…
              </div>
            )}
            <div ref={fimRef} />
          </div>

          {/* chips de sugestão / atalhos rápidos */}
          <div className="robson-chips">
            {[
              { txt: '📦 Anúncios de um SKU', cmd: 'anuncios ' },
              { txt: '🔴 Pausar variação', cmd: 'pausar variação ' },
              { txt: '🟢 Ativar variação', cmd: 'ativar variação ' },
              { txt: '📦 Alterar saldo', cmd: 'alterar saldo ' },
              { txt: '💰 Vendas de hoje', cmd: 'qual o total de vendas de hoje?' },
              { txt: '📊 Pedidos de hoje', cmd: 'quantos pedidos foram atendidos hoje?' },
            ].map((c, i) => (
              <button key={i} type="button" className="robson-chip"
                onClick={() => {
                  // comandos que terminam com espaço são "modelos": coloca no input pra completar
                  if (c.cmd.endsWith(' ')) { setPergunta(c.cmd); inputRef.current?.focus() }
                  else enviar(c.cmd)
                }}>
                {c.txt}
              </button>
            ))}
          </div>

          <div style={estilos.rodape} className="robson-rodape">
            <input
              ref={inputRef}
              style={estilos.input}
              value={pergunta}
              onChange={(e) => setPergunta(e.target.value)}
              onKeyDown={onKey}
              placeholder="Pergunte ao Robson…"
              disabled={carregando}
            />
            <button
              style={{ ...estilos.enviar, opacity: carregando ? 0.5 : 1 }}
              onClick={() => enviar()}
              disabled={carregando}
            >
              ➤
            </button>
          </div>
          </div>
        </>
      )}
    </>
  )
}

// ============================================
// CardRevisao — 3 grupos de checkboxes (anúncios/variações/kits) antes de agir
// ============================================
function CardRevisao({ dados, conta, apiUrl, iduser, onExecutar }) {
  const acao = dados.acao
  // marca tudo por padrão
  const [selAnun, setSelAnun] = useState(() => (dados.anunciosDiretos || []).map((_, i) => i))
  const [selVar, setSelVar] = useState(() => (dados.variacoes || []).map((_, i) => i))
  const [selKit, setSelKit] = useState(() => (dados.kits || []).map((_, i) => i))
  const [feito, setFeito] = useState(false)

  const toggle = (arr, setArr, i) =>
    setArr(arr.includes(i) ? arr.filter((x) => x !== i) : [...arr, i])

  // marca ou desmarca TODOS de um grupo
  const todosGrupo = (lista, sel, setSel) => {
    if (sel.length === lista.length) setSel([])          // todos marcados -> desmarca
    else setSel(lista.map((_, i) => i))                  // marca todos
  }

  const totalSel = selAnun.length + selVar.length + selKit.length

  function confirmar() {
    if (!totalSel || feito) return
    setFeito(true)
    onExecutar({
      acao,
      sku: dados.sku,
      novoSaldo: dados.novoSaldo,
      _qtd: dados.qtdAtivar,        // quantidade já informada no comando "ativar X 600"
      anuncios: selAnun.map((i) => dados.anunciosDiretos[i]),
      variacoes: selVar.map((i) => dados.variacoes[i]),
      kits: selKit.map((i) => dados.kits[i]),
    })
  }

  const ehSaldo = acao === 'saldo'
  const verbo = ehSaldo ? `alterar saldo p/ ${dados.novoSaldo}` : (acao === 'pausar' ? 'pausar' : 'ativar')
  const cor = ehSaldo ? '#1e40af' : (acao === 'pausar' ? '#991b1b' : '#166534')
  const icone = ehSaldo ? '📦' : (acao === 'pausar' ? '🔴' : '🟢')

  return (
    <div className="revisao-card">
      <div className="revisao-titulo" style={{ color: cor }}>
        {icone} Revisar o que vou {verbo} — SKU {dados.sku}
      </div>

      {/* botão geral: marcar/desmarcar TUDO */}
      <div className="revisao-geral">
        <button type="button" className="rev-todos-btn" disabled={feito}
          onClick={() => {
            const tudoMarcado = totalSel === (dados.anunciosDiretos?.length || 0) + (dados.variacoes?.length || 0) + (dados.kits?.length || 0)
            if (tudoMarcado) { setSelAnun([]); setSelVar([]); setSelKit([]) }
            else {
              setSelAnun((dados.anunciosDiretos || []).map((_, i) => i))
              setSelVar((dados.variacoes || []).map((_, i) => i))
              setSelKit((dados.kits || []).map((_, i) => i))
            }
          }}>
          ☑️ Marcar / desmarcar tudo
        </button>
      </div>

      {/* GRUPO: Anúncios diretos */}
      {(dados.anunciosDiretos || []).length > 0 && (
        <div className="revisao-grupo">
          <div className="revisao-grupo-titulo">
            <span>📦 Anúncios ({selAnun.length}/{dados.anunciosDiretos.length})</span>
            <button type="button" className="rev-grupo-btn" disabled={feito}
              onClick={() => todosGrupo(dados.anunciosDiretos, selAnun, setSelAnun)}>
              {selAnun.length === dados.anunciosDiretos.length ? 'desmarcar todos' : 'marcar todos'}
            </button>
          </div>
          {dados.anunciosDiretos.map((a, i) => (
            <label key={i} className="revisao-item">
              <input type="checkbox" checked={selAnun.includes(i)} disabled={feito}
                onChange={() => toggle(selAnun, setSelAnun, i)} />
              <span className="ri-nome" title={a.titulo}>{a.titulo || a.mlb}</span>
              <a href={a.link} target="_blank" rel="noopener noreferrer" className="ri-link">{a.mlb}</a>
              <span className="ri-conta">{a.conta}</span>
            </label>
          ))}
        </div>
      )}

      {/* GRUPO: Variações */}
      {(dados.variacoes || []).length > 0 && (
        <div className="revisao-grupo">
          <div className="revisao-grupo-titulo">
            <span>🎨 Variações ({selVar.length}/{dados.variacoes.length})</span>
            <button type="button" className="rev-grupo-btn" disabled={feito}
              onClick={() => todosGrupo(dados.variacoes, selVar, setSelVar)}>
              {selVar.length === dados.variacoes.length ? 'desmarcar todos' : 'marcar todos'}
            </button>
          </div>
          {dados.variacoes.map((v, i) => (
            <label key={i} className="revisao-item">
              <input type="checkbox" checked={selVar.includes(i)} disabled={feito}
                onChange={() => toggle(selVar, setSelVar, i)} />
              <span className="ri-nome">{v.combinacao || 'variação'}</span>
              <a href={v.link || `https://produto.mercadolivre.com.br/${v.mlb}`} target="_blank" rel="noopener noreferrer" className="ri-link">{v.mlb}</a>
              <span className="ri-conta">{v.conta}</span>
            </label>
          ))}
        </div>
      )}

      {/* GRUPO: Kits */}
      {(dados.kits || []).length > 0 && (
        <div className="revisao-grupo">
          <div className="revisao-grupo-titulo">
            <span>🧩 Kits que usam o SKU ({selKit.length}/{dados.kits.length})</span>
            <button type="button" className="rev-grupo-btn" disabled={feito}
              onClick={() => todosGrupo(dados.kits, selKit, setSelKit)}>
              {selKit.length === dados.kits.length ? 'desmarcar todos' : 'marcar todos'}
            </button>
          </div>
          {dados.kits.map((k, i) => (
            <label key={i} className="revisao-item">
              <input type="checkbox" checked={selKit.includes(i)} disabled={feito}
                onChange={() => toggle(selKit, setSelKit, i)} />
              <span className="ri-nome" title={k.descricao}>
                {k.descricao || k.kitSku}
                {k.combinacao ? ` · ${k.combinacao}` : ''}
              </span>
              <span className={`ri-tag ${k.acao_kit === 'anuncio' && acao === 'pausar' ? 'tag-alerta' : 'tag-ok'}`}>
                {(() => {
                  if (ehSaldo) return `saldo → ${dados.novoSaldo}`
                  if (acao === 'ativar') return k.acao_kit === 'anuncio' ? 'ativa o kit' : 'repõe a variação'
                  // pausar
                  return k.acao_kit === 'anuncio' ? `pausa o kit (${k.motivo})` : 'só a variação'
                })()}
              </span>
            </label>
          ))}
        </div>
      )}

      {!feito ? (
        <button type="button" className="revisao-btn" onClick={confirmar} disabled={!totalSel}
          style={{ background: cor }}>
          {icone} Confirmar e {verbo} {totalSel} item(ns)
        </button>
      ) : (
        <p style={{ margin: '8px 0 0', color: cor, fontWeight: 700 }}>Executando…</p>
      )}
    </div>
  )
}

// ============================================
// PainelVendidos — abas por conta + status ML + abrir painel do SKU
// ============================================
function PainelVendidos({ itens, dias, conta, apiUrl, onAbrirSku }) {
  // agrupa por apelido (conta)
  const porConta = {}
  for (const it of itens) {
    const k = it.apelido || '—'
    if (!porConta[k]) porConta[k] = []
    porConta[k].push(it)
  }
  const contas = Object.keys(porConta).sort()
  const [abaAtiva, setAbaAtiva] = useState(contas[0] || '')
  const [statusML, setStatusML] = useState({}) // sku -> {ativos,pausados,total}
  const [verificando, setVerificando] = useState(false)
  const [progresso, setProgresso] = useState(null) // { feitos, total }

  const listaAba = porConta[abaAtiva] || []

  // verifica status ML dos SKUs da aba ativa (em lotes, com progresso)
  async function verificarStatus() {
    if (verificando) return
    setVerificando(true)
    const pendentes = listaAba.filter((it) => !statusML[it.sku])
    let feitos = 0
    setProgresso({ feitos: 0, total: pendentes.length })
    try {
      for (let i = 0; i < pendentes.length; i += 5) {
        const lote = pendentes.slice(i, i + 5)
        try {
          const r = await fetch(`${apiUrl}/api/ia/acao/status-skus/${conta}`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ skus: lote.map((it) => ({ sku: it.sku, seller: it.id_conta })) })
          })
          if (!r.ok) throw new Error('rota')
          const d = await r.json()
          setStatusML((s) => ({ ...s, ...(d.status || {}) }))
        } catch (e) {
          const marca = {}
          lote.forEach((it) => { marca[it.sku] = { total: 0, ativos: 0, pausados: 0, erro: true } })
          setStatusML((s) => ({ ...s, ...marca }))
        }
        feitos += lote.length
        setProgresso({ feitos, total: pendentes.length })
      }
    } finally {
      setVerificando(false)
      setProgresso(null)
    }
  }

  const badgeStatus = (sku) => {
    const s = statusML[sku]
    if (!s) return <span className="badge b-wait">—</span>
    if (s.erro) return <span className="badge b-off">erro</span>
    if (s.total === 0) return <span className="badge b-wait">sem anúncio</span>
    if (s.pausados > 0 && s.ativos === 0) return <span className="badge b-off">🔴 {s.pausados} pausado(s)</span>
    if (s.pausados > 0) return <span className="badge b-mix">🟡 {s.ativos}🟢 {s.pausados}🔴</span>
    return <span className="badge b-on">🟢 {s.ativos} ativo(s)</span>
  }

  return (
    <div className="robson-balao robson-html vend-painel">
      <p style={{ margin: '0 0 8px', fontWeight: 700 }}>
        📊 Vendidos — últimos {dias} dias ({itens.length} itens)
      </p>

      {/* abas por conta */}
      <div className="vend-abas">
        {contas.map((c) => (
          <button key={c} type="button"
            className={`vend-aba ${c === abaAtiva ? 'ativa' : ''}`}
            onClick={() => setAbaAtiva(c)}>
            {c} <span className="vend-aba-qtd">{porConta[c].length}</span>
          </button>
        ))}
      </div>

      <div className="vend-topo-acoes">
        <button type="button" className="massa-btn" onClick={verificarStatus} disabled={verificando}>
          {verificando
            ? `Verificando ${progresso ? `${progresso.feitos}/${progresso.total}` : ''}…`
            : '🔎 Verificar status no ML'}
        </button>
      </div>

      <table className="vend-tabela">
        <thead>
          <tr><th>SKU</th><th>Produto</th><th>Qtd</th><th>Status ML</th><th></th></tr>
        </thead>
        <tbody>
          {listaAba.map((it, idx) => (
            <tr key={idx}>
              <td><b>{it.sku}</b></td>
              <td className="vend-desc" title={it.descricao}>{it.descricao || '—'}</td>
              <td style={{ textAlign: 'right' }}>{it.qtd_vendida}</td>
              <td>{badgeStatus(it.sku)}</td>
              <td>
                <button type="button" className="vend-abrir"
                  onClick={() => onAbrirSku(it.sku)}>
                  abrir ▸
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// ============================================
// PainelAnuncios — pai + variações (filhos) editáveis
// ============================================
function PainelAnuncios({ anuncios, sku, conta, apiUrl, iduser, onResultado }) {
  // edits do PAI (anúncio sem variação): { [mlb]: { price, quantity, status } }
  const [edits, setEdits] = useState({})
  // variações carregadas: { [mlb]: { loading, temVariacao, variations:[...] } }
  const [vars, setVars] = useState({})
  // edits das variações: { [mlb]: { [idVar]: { price, quantity } } }
  const [varEdits, setVarEdits] = useState({})
  const [expandido, setExpandido] = useState({}) // { [mlb]: true }
  const [enviando, setEnviando] = useState(false)
  const [feito, setFeito] = useState(false)
  const [sel, setSel] = useState({})          // { [mlb]: true }
  const [massaPct, setMassaPct] = useState('') // % de preço
  const [massaQtd, setMassaQtd] = useState('') // qtd fixa
  const [aviso, setAviso] = useState('')       // aviso amigável (ex: sem estoque)
  const [skuZerar, setSkuZerar] = useState('')  // SKU da variação a zerar
  const [zerando, setZerando] = useState(false)
  const [rapidoTermo, setRapidoTermo] = useState('') // termo da ação rápida
  const [rapidoQtd, setRapidoQtd] = useState('')     // qtd opcional ao ativar
  const [rapidoBusy, setRapidoBusy] = useState(false)
  const [varAtivarQtd, setVarAtivarQtd] = useState('') // qtd pra ativar variação
  const [varBusy, setVarBusy] = useState(null)          // id da variação em ação (linha)

  const setEdit = (mlb, campo, valor) =>
    setEdits((e) => ({ ...e, [mlb]: { ...e[mlb], [campo]: valor } }))

  const setVarEdit = (mlb, idVar, campo, valor) =>
    setVarEdits((e) => ({
      ...e,
      [mlb]: { ...e[mlb], [idVar]: { ...(e[mlb]?.[idVar]), [campo]: valor } }
    }))

  async function toggleVariacoes(a) {
    const mlb = a.mlb
    setExpandido((x) => ({ ...x, [mlb]: !x[mlb] }))
    if (vars[mlb]) return // já carregou
    setVars((v) => ({ ...v, [mlb]: { loading: true } }))
    try {
      const r = await fetch(`${apiUrl}/api/ia/acao/variacoes/${conta}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mlb, seller: a.seller })
      })
      const d = await r.json()
      setVars((v) => ({ ...v, [mlb]: { loading: false, ...d } }))
    } catch {
      setVars((v) => ({ ...v, [mlb]: { loading: false, ok: false, erro: 'falhou' } }))
    }
  }

  // alterações do PAI (status/preço/qtd/sku em anúncio sem variação)
  const norm = (s) => String(s || '').trim().toLowerCase()
  const mudouPai = (a) => {
    const e = edits[a.mlb]
    if (!e) return false
    return (e.price !== undefined && e.price !== '' && Number(e.price) !== Number(a.preco))
      || (e.quantity !== undefined && e.quantity !== '' && Number(e.quantity) !== Number(a.qtd))
      || (e.status !== undefined && norm(e.status) !== norm(a.status))
      || (e.novoSku !== undefined && String(e.novoSku) !== String(a.skuAnuncio || ''))
  }

  const alteracoesPai = anuncios.filter(mudouPai).map((a) => {
    const e = edits[a.mlb]
    const alt = { seller: a.seller, mlb: a.mlb, sku, origem: a.origem }
    if (e.price !== undefined && e.price !== '' && Number(e.price) !== Number(a.preco)) alt.price = e.price
    if (e.quantity !== undefined && e.quantity !== '' && Number(e.quantity) !== Number(a.qtd)) alt.quantity = e.quantity
    if (e.status !== undefined && norm(e.status) !== norm(a.status)) alt.status = e.status
    if (e.novoSku !== undefined && String(e.novoSku) !== String(a.skuAnuncio || '')) alt.novoSku = e.novoSku
    return alt
  })

  // conta só variações realmente alteradas (compara com o valor original)
  const contarVarMudadas = () => {
    let n = 0
    for (const a of anuncios) {
      const info = vars[a.mlb]
      const mud = varEdits[a.mlb]
      if (!info?.variations || !mud) continue
      for (const v of info.variations) {
        const ve = mud[v.id]
        if (!ve) continue
        const precoMudou = ve.price !== undefined && ve.price !== '' && Number(ve.price) !== Number(v.price)
        const qtdMudou = ve.quantity !== undefined && ve.quantity !== '' && Number(ve.quantity) !== Number(v.quantity)
        const skuMudou = ve.sku !== undefined && String(ve.sku) !== String(v.sku || '')
        if (precoMudou || qtdMudou || skuMudou) n++
      }
    }
    return n
  }
  const totalVarEdits = contarVarMudadas()

  const temAlgo = alteracoesPai.length > 0 || totalVarEdits > 0

  async function confirmar() {
    if (!temAlgo || enviando) return
    setEnviando(true)
    const partes = []
    try {
      // 1) alterações do pai (lote)
      if (alteracoesPai.length) {
        const r = await fetch(`${apiUrl}/api/ia/acao/editar/${conta}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ alteracoes: alteracoesPai, iduser })
        })
        const d = await r.json()
        partes.push(d.html || '')
      }
      // 2) variações — só as que realmente mudaram, uma chamada por MLB
      for (const a of anuncios) {
        const info = vars[a.mlb]
        const mud = varEdits[a.mlb]
        if (!info?.variations || !mud) continue
        // filtra só as variações com mudança real
        const mudancas = {}
        for (const v of info.variations) {
          const ve = mud[v.id]
          if (!ve) continue
          const alt = {}
          if (ve.price !== undefined && ve.price !== '' && Number(ve.price) !== Number(v.price)) alt.price = ve.price
          if (ve.quantity !== undefined && ve.quantity !== '' && Number(ve.quantity) !== Number(v.quantity)) alt.quantity = ve.quantity
          if (ve.sku !== undefined && String(ve.sku) !== String(v.sku || '')) alt.sku = ve.sku
          if (Object.keys(alt).length) mudancas[v.id] = alt
        }
        if (!Object.keys(mudancas).length) continue
        const r = await fetch(`${apiUrl}/api/ia/acao/variacoes/salvar/${conta}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ mlb: a.mlb, seller: a.seller, sku, mudancas, iduser })
        })
        const d = await r.json()
        partes.push(d.html || '')
      }
      setFeito(true)
      onResultado?.(partes.join('') || '<p>Alterações enviadas.</p>')
    } catch {
      onResultado?.('<p>Erro ao aplicar alterações.</p>')
    } finally {
      setEnviando(false)
    }
  }

  // ---- seleção ----
  const selecionados = anuncios.filter((a) => sel[a.mlb])
  const todosSel = anuncios.length > 0 && selecionados.length === anuncios.length
  const toggleSel = (mlb) => setSel((s) => ({ ...s, [mlb]: !s[mlb] }))
  const toggleTodos = () => {
    if (todosSel) { setSel({}) }
    else {
      const novo = {}
      anuncios.forEach((a) => { novo[a.mlb] = true })
      setSel(novo)
    }
  }

  // ---- ações em massa (aplicam nos SELECIONADOS; só preparam, não enviam) ----
  const massaStatus = (status) => {
    if (!selecionados.length) return
    setEdits((e) => {
      const n = { ...e }
      selecionados.forEach((a) => { n[a.mlb] = { ...n[a.mlb], status } })
      return n
    })
    // aviso: ativar anúncio sem estoque não funciona (regra do ML)
    if (status === 'active') {
      const semEstoque = selecionados.filter((a) => {
        const info = vars[a.mlb]
        if (info?.temVariacao && info.variations?.length) {
          // todas as variações zeradas (considerando edições)
          return info.variations.every((v) => {
            const q = varEdits[a.mlb]?.[v.id]?.quantity
            return Number(q !== undefined ? q : v.quantity) <= 0
          })
        }
        const q = edits[a.mlb]?.quantity
        return Number(q !== undefined ? q : a.qtd) <= 0
      })
      if (semEstoque.length) {
        setAviso(`${semEstoque.length} anúncio(s) estão sem estoque. Pra ativar, coloque quantidade — o ML reativa sozinho.`)
      }
    }
  }
  const massaPreco = (sinal) => {
    const pct = parseFloat(String(massaPct).replace(',', '.'))
    if (!selecionados.length || isNaN(pct)) return
    const fator = 1 + (sinal * pct) / 100

    selecionados.forEach((a) => {
      const info = vars[a.mlb]
      // anúncio COM variação carregada: aplica em cada variação
      if (info && info.temVariacao && info.variations?.length) {
        setVarEdits((e) => {
          const n = { ...e, [a.mlb]: { ...e[a.mlb] } }
          info.variations.forEach((v) => {
            // usa o valor já editado, senão o preço atual da variação
            const base = Number(n[a.mlb]?.[v.id]?.price ?? v.price) || 0
            n[a.mlb][v.id] = { ...n[a.mlb][v.id], price: +(base * fator).toFixed(2) }
          })
          return n
        })
      } else {
        // anúncio simples: aplica no pai
        setEdits((e) => {
          const base = Number(e[a.mlb]?.price ?? a.preco) || 0
          return { ...e, [a.mlb]: { ...e[a.mlb], price: +(base * fator).toFixed(2) } }
        })
      }
    })
  }
  const massaQuantidade = () => {
    const q = parseInt(String(massaQtd), 10)
    if (!selecionados.length || isNaN(q)) return
    selecionados.forEach((a) => {
      const info = vars[a.mlb]
      if (info && info.temVariacao && info.variations?.length) {
        setVarEdits((e) => {
          const n = { ...e, [a.mlb]: { ...e[a.mlb] } }
          info.variations.forEach((v) => {
            n[a.mlb][v.id] = { ...n[a.mlb][v.id], quantity: q }
          })
          return n
        })
      } else {
        setEdits((e) => ({ ...e, [a.mlb]: { ...e[a.mlb], quantity: q } }))
      }
    })
  }

  // zera o estoque de TODAS as variações (e do pai se for simples) dos selecionados
  const zerarEstoque = () => {
    const alvos = selecionados.length ? selecionados : anuncios
    if (!alvos.length) return
    alvos.forEach((a) => {
      const info = vars[a.mlb]
      if (info && info.temVariacao && info.variations?.length) {
        setVarEdits((e) => {
          const n = { ...e, [a.mlb]: { ...e[a.mlb] } }
          info.variations.forEach((v) => {
            n[a.mlb][v.id] = { ...n[a.mlb][v.id], quantity: 0 }
          })
          return n
        })
      } else {
        setEdits((e) => ({ ...e, [a.mlb]: { ...e[a.mlb], quantity: 0 } }))
      }
    })
    setAviso('Estoque zerado nas variações. Revise e clique em Confirmar para enviar.')
  }

  // ---- AÇÃO RÁPIDA: digita termo (sku/mlb/variação/descrição) e ativa/pausa ----
  // acao: 'ativar' | 'pausar' ; qtdAtivar: quantidade opcional (só no ativar)
  async function acaoRapida(acao, qtdAtivar) {
    const termo = rapidoTermo.trim()
    if (!termo || rapidoBusy) return
    setRapidoBusy(true)
    const alvoUp = termo.toUpperCase()
    const status = acao === 'ativar' ? 'active' : 'paused'
    const qtd = (qtdAtivar !== undefined && qtdAtivar !== '' && !isNaN(Number(qtdAtivar)))
      ? Number(qtdAtivar) : undefined

    try {
      // garante variações carregadas (pra achar por SKU de variação)
      const carregadas = { ...vars }
      for (const a of anuncios) {
        if (carregadas[a.mlb]) continue
        try {
          const r = await fetch(`${apiUrl}/api/ia/acao/variacoes/${conta}`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ mlb: a.mlb, seller: a.seller })
          })
          carregadas[a.mlb] = { loading: false, ...(await r.json()) }
        } catch { /* ignora */ }
      }
      setVars(carregadas)

      // 1) é MLB?
      const ehMlb = /^MLB\s*\d{6,}$/i.test(alvoUp.replace(/\s/g, ''))
      // 2) acha anúncios/variações que batem com o termo
      const alvosAnuncio = [] // agir no anúncio inteiro
      const alvosVariacao = [] // { mlb, seller, idVar }

      for (const a of anuncios) {
        const info = carregadas[a.mlb]
        const mlbBate = ehMlb && a.mlb.toUpperCase() === alvoUp.replace(/\s/g, '')
        const skuAnuncioBate = String(a.skuAnuncio || a.sku || '').toUpperCase() === alvoUp
        const descBate = !ehMlb && (a.titulo || '').toUpperCase().includes(alvoUp) && alvoUp.length >= 4

        // variação que bate pelo SKU
        if (info?.variations) {
          for (const v of info.variations) {
            if (String(v.sku || '').toUpperCase().trim() === alvoUp) {
              alvosVariacao.push({ mlb: a.mlb, seller: a.seller, idVar: v.id, conta: a.conta })
            }
          }
        }
        if (mlbBate || skuAnuncioBate || descBate) {
          alvosAnuncio.push(a)
        }
      }

      if (!alvosAnuncio.length && !alvosVariacao.length) {
        onResultado?.(`<p>Não achei nada com "<b>${termo}</b>" (nem SKU, MLB, variação ou descrição).</p>`)
        setRapidoBusy(false)
        return
      }

      const partes = []

      // age nas VARIAÇÕES encontradas
      for (const e of alvosVariacao) {
        // ativar variação = colocar quantidade (>0); pausar = zerar
        const q = acao === 'ativar' ? (qtd && qtd > 0 ? qtd : 1) : 0
        const r = await fetch(`${apiUrl}/api/ia/acao/variacoes/salvar/${conta}`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ mlb: e.mlb, seller: e.seller, sku: alvoUp, mudancas: { [e.idVar]: { quantity: q } }, iduser })
        })
        const d = await r.json()
        partes.push(d.ok ? `✅ ${e.mlb} (${e.conta}) — variação ${acao === 'ativar' ? `ativada (qtd ${q})` : 'pausada (0)'}`
                         : `❌ ${e.mlb}: ${d.erro || 'erro'}`)
      }

      // age nos ANÚNCIOS encontrados
      if (alvosAnuncio.length) {
        const alteracoes = alvosAnuncio.map((a) => {
          const alt = { seller: a.seller, mlb: a.mlb, sku: a.sku, origem: a.origem, status }
          if (acao === 'ativar' && qtd !== undefined) alt.quantity = qtd
          return alt
        })
        const r = await fetch(`${apiUrl}/api/ia/acao/editar/${conta}`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ alteracoes, iduser })
        })
        const d = await r.json()
        partes.push(d.html ? d.html.replace(/<\/?p>/g, '') : `${d.ok || 0} anúncio(s) ${acao === 'ativar' ? 'ativado(s)' : 'pausado(s)'}`)
      }

      onResultado?.(`<p><b>${acao === 'ativar' ? '🟢 Ativação' : '🔴 Pausa'} rápida — "${termo}"</b></p>` +
        `<p><small>${partes.join('<br>')}</small></p>`)
      setRapidoTermo('')
    } catch {
      onResultado?.('<p>Erro na ação rápida.</p>')
    } finally {
      setRapidoBusy(false)
    }
  }

  // busca a variação pelo SKU (nas variações já carregadas), zera e envia
  async function mexerVarPorSku(qtdDesejada) {
    const alvo = skuZerar.trim().toUpperCase()
    if (!alvo || zerando) return
    setZerando(true)
    try {
      // garante que as variações de todos os anúncios estão carregadas
      const faltam = anuncios.filter((a) => !vars[a.mlb])
      const carregadas = { ...vars }
      for (const a of faltam) {
        try {
          const r = await fetch(`${apiUrl}/api/ia/acao/variacoes/${conta}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ mlb: a.mlb, seller: a.seller })
          })
          carregadas[a.mlb] = { loading: false, ...(await r.json()) }
        } catch { /* ignora */ }
      }
      setVars(carregadas)

      // procura em todos os anúncios qual variação tem esse SKU
      const encontrados = [] // { mlb, seller, idVar }
      for (const a of anuncios) {
        const info = carregadas[a.mlb]
        if (!info?.variations) continue
        for (const v of info.variations) {
          if (String(v.sku || '').toUpperCase().trim() === alvo) {
            encontrados.push({ mlb: a.mlb, seller: a.seller, idVar: v.id, conta: a.conta })
          }
        }
      }

      if (!encontrados.length) {
        onResultado?.(`<p>Não achei a variação de SKU <b>${skuZerar}</b> nos anúncios carregados. ` +
          `Confira se o SKU está certo e se as variações já carregaram.</p>`)
        setZerando(false)
        return
      }

      const acaoTxt = qtdDesejada > 0 ? `ativada (qtd ${qtdDesejada})` : 'zerada (pausada)'
      const partes = []
      for (const e of encontrados) {
        const r = await fetch(`${apiUrl}/api/ia/acao/variacoes/salvar/${conta}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            mlb: e.mlb, seller: e.seller, sku: alvo,
            mudancas: { [e.idVar]: { quantity: qtdDesejada } },
            iduser
          })
        })
        const d = await r.json()
        partes.push(d.ok
          ? `✅ ${e.mlb} (${e.conta}) — variação ${acaoTxt}`
          : `❌ ${e.mlb}: ${d.erro || 'erro'}`)
      }

      onResultado?.(`<p><b>Variação ${skuZerar}</b> — ${encontrados.length} anúncio(s):</p>` +
        `<p><small>${partes.join('<br>')}</small></p>`)
      setSkuZerar('')
    } catch {
      onResultado?.('<p>Erro ao mexer na variação.</p>')
    } finally {
      setZerando(false)
    }
  }

  const zerarVarPorSku = () => mexerVarPorSku(0)
  const ativarVarPorSku = () => {
    const q = parseInt(String(varAtivarQtd), 10)
    mexerVarPorSku(isNaN(q) || q <= 0 ? 1 : q)
  }

  // pausar/ativar a variação clicando no botão da linha
  async function acaoVariacaoLinha(a, v, acao) {
    if (varBusy) return
    // ativar: pergunta a quantidade (via prompt simples); pausar: zera direto
    let quantidade = 0
    if (acao === 'ativar') {
      const resp = window.prompt(`Ativar variação ${v.sku || v.id} com quantas unidades?`, '10')
      if (resp === null) return // cancelou
      quantidade = parseInt(resp, 10)
      if (isNaN(quantidade) || quantidade <= 0) { onResultado?.('<p>Quantidade inválida.</p>'); return }
    }
    setVarBusy(v.id)
    try {
      const r = await fetch(`${apiUrl}/api/ia/acao/variacoes/salvar/${conta}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mlb: a.mlb, seller: a.seller, sku: v.sku || '', mudancas: { [v.id]: { quantity: quantidade } }, iduser })
      })
      const d = await r.json()
      if (d.ok) {
        // atualiza o valor exibido na tela pra refletir a mudança
        setVarEdit(a.mlb, v.id, 'quantity', quantidade)
        onResultado?.(`<p>✅ Variação <b>${v.combinacao || v.sku}</b> de ${a.mlb} — ${acao === 'ativar' ? `ativada com ${quantidade}` : 'pausada (0)'}.</p>`)
      } else {
        onResultado?.(`<p>❌ ${a.mlb}: ${d.erro || 'erro'}</p>`)
      }
    } catch {
      onResultado?.('<p>Erro ao mexer na variação.</p>')
    } finally {
      setVarBusy(null)
    }
  }

  const ativos = anuncios.filter((a) => a.status === 'active').length
  const pausados = anuncios.filter((a) => a.status === 'paused').length

  // ao montar: carrega e abre as variações de todos os anúncios automaticamente
  useEffect(() => {
    let cancelado = false
    ;(async () => {
      for (const a of anuncios) {
        if (cancelado) break
        try {
          const r = await fetch(`${apiUrl}/api/ia/acao/variacoes/${conta}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ mlb: a.mlb, seller: a.seller })
          })
          const d = await r.json()
          if (cancelado) break
          setVars((v) => ({ ...v, [a.mlb]: { loading: false, ...d } }))
          // só abre quem tem variação (anúncio simples fica fechado)
          if (d.temVariacao) setExpandido((x) => ({ ...x, [a.mlb]: true }))
        } catch {
          if (!cancelado) setVars((v) => ({ ...v, [a.mlb]: { loading: false, ok: false } }))
        }
      }
    })()
    return () => { cancelado = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div className="robson-balao robson-html" style={{ alignSelf: 'flex-start', maxWidth: '100%', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 14, padding: '9px 13px', boxSizing: 'border-box' }}>
      <p style={{ margin: '3px 0' }}>
        <b>SKU {sku}</b> — {anuncios.length} anúncio(s): 🟢 {ativos} · 🔴 {pausados}
      </p>

      {/* ---- ação rápida: digita sku/mlb/variação/descrição e ativa/pausa ---- */}
      {!feito && (
        <div className="massa-bar" style={{ background: '#eff6ff', borderColor: '#bfdbfe' }}>
          <span className="massa-lbl">⚡ Rápido:</span>
          <input className="inp-sku" style={{ width: 200 }} type="text"
            placeholder="SKU, MLB, SKU variação ou descrição"
            value={rapidoTermo} onChange={(e) => setRapidoTermo(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') acaoRapida('ativar', rapidoQtd) }} />
          <button type="button" className="massa-btn st-on" onClick={() => acaoRapida('ativar', rapidoQtd)} disabled={rapidoBusy}>
            {rapidoBusy ? '…' : '🟢 Ativar'}
          </button>
          <button type="button" className="massa-btn st-off" onClick={() => acaoRapida('pausar')} disabled={rapidoBusy}>
            {rapidoBusy ? '…' : '🔴 Pausar'}
          </button>
          <span className="massa-lbl">c/ qtd</span>
          <input className="inp-num massa-inp" type="number" step="1" placeholder="qtd"
            value={rapidoQtd} onChange={(e) => setRapidoQtd(e.target.value)} />
        </div>
      )}

      {/* ---- zerar variação por SKU (busca, zera e envia) ---- */}
      {!feito && (
        <div className="massa-bar" style={{ background: '#fef2f2', borderColor: '#fecaca' }}>
          <span className="massa-lbl">Variação por SKU:</span>
          <input className="inp-sku" type="text" placeholder="SKU da variação"
            value={skuZerar} onChange={(e) => setSkuZerar(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') zerarVarPorSku() }} />
          <button type="button" className="massa-btn st-off" onClick={zerarVarPorSku} disabled={zerando}>
            {zerando ? 'Zerando…' : '0️⃣ Zerar (pausar)'}
          </button>
          <input className="inp-num massa-inp" type="number" step="1" placeholder="qtd"
            value={varAtivarQtd} onChange={(e) => setVarAtivarQtd(e.target.value)} />
          <button type="button" className="massa-btn st-on" onClick={ativarVarPorSku} disabled={zerando}>
            🟢 Ativar
          </button>
        </div>
      )}

      {/* ---- barra de ações em massa ---- */}
      {!feito && (
        <div className="massa-bar">
          <span className="massa-info">{selecionados.length} selecionado(s)</span>
          <div className="massa-grupo">
            <button type="button" className="massa-btn st-on" onClick={() => massaStatus('active')}>🟢 Ativar</button>
            <button type="button" className="massa-btn st-off" onClick={() => massaStatus('paused')}>🔴 Pausar</button>
          </div>
          <div className="massa-grupo">
            <span className="massa-lbl">Preço</span>
            <input className="inp-num massa-inp" type="number" step="0.1" placeholder="%"
              value={massaPct} onChange={(e) => setMassaPct(e.target.value)} />
            <button type="button" className="massa-btn" onClick={() => massaPreco(+1)} title="Aumentar %">+%</button>
            <button type="button" className="massa-btn" onClick={() => massaPreco(-1)} title="Diminuir %">−%</button>
          </div>
          <div className="massa-grupo">
            <span className="massa-lbl">Qtd</span>
            <input className="inp-num massa-inp" type="number" step="1" placeholder="0"
              value={massaQtd} onChange={(e) => setMassaQtd(e.target.value)} />
            <button type="button" className="massa-btn" onClick={massaQuantidade} title="Aplicar">Aplicar</button>
          </div>
          <div className="massa-grupo">
            <button type="button" className="massa-btn st-off" onClick={zerarEstoque}
              title="Zera o estoque de todas as variações dos selecionados">
              0️⃣ Zerar estoque
            </button>
          </div>
        </div>
      )}

      {aviso && !feito && (
        <div className="painel-aviso">⚠️ {aviso}</div>
      )}

      <div className="cards-lista">
        {anuncios.map((a) => {
          const e = edits[a.mlb] || {}
          const statusAtual = e.status ?? a.status
          const img = a.img ? String(a.img).replace(/^http:/, 'https:') : null
          const info = vars[a.mlb]
          const aberto = expandido[a.mlb]
          const ativoPai = String(statusAtual).toLowerCase() === 'active'
          return (
            <div key={a.mlb} className={`anuncio-card ${mudouPai(a) ? 'card-mudado' : ''}`}>
              {/* cabeçalho do card (anúncio pai) */}
              <div className="card-topo">
                <input type="checkbox" className="card-chk" checked={!!sel[a.mlb]}
                  onChange={() => toggleSel(a.mlb)} disabled={feito} />
                {img ? <img src={img} alt="" className="card-thumb" loading="lazy" />
                     : <div className="card-thumb" />}
                <div className="card-info">
                  <div className="card-titulo" title={a.titulo}>{a.titulo}</div>
                  <div className="card-meta">
                    <a href={a.link} target="_blank" rel="noopener noreferrer" className="card-mlb">{a.mlb}</a>
                    <span className="card-conta">{a.conta}</span>
                    <span className={`badge ${ativoPai ? 'b-on' : 'b-off'}`}>
                      {ativoPai ? '🟢 Ativo' : '🔴 Pausado'}
                    </span>
                  </div>
                </div>
                <button type="button"
                  className={`btn-status ${ativoPai ? 'st-on' : 'st-off'}`}
                  disabled={feito}
                  onClick={() => setEdit(a.mlb, 'status', ativoPai ? 'paused' : 'active')}>
                  {ativoPai ? 'Pausar' : 'Ativar'}
                </button>
              </div>

              {/* campos do anúncio pai */}
              <div className="card-campos">
                <label>SKU
                  <input type="text" className="inp-sku"
                    value={e.novoSku !== undefined ? e.novoSku : (a.skuAnuncio || '')}
                    disabled={feito} onChange={(ev) => setEdit(a.mlb, 'novoSku', ev.target.value)} />
                </label>
                <label>Preço
                  <input type="number" step="0.01" min="0" className="inp-num"
                    value={e.price !== undefined ? e.price : a.preco} disabled={feito}
                    onChange={(ev) => setEdit(a.mlb, 'price', ev.target.value)} />
                </label>
                <label>Qtd
                  <input type="number" step="1" min="0" className="inp-num"
                    value={e.quantity !== undefined ? e.quantity : a.qtd} disabled={feito}
                    onChange={(ev) => setEdit(a.mlb, 'quantity', ev.target.value)} />
                </label>
                {info?.temVariacao && (
                  <button type="button" className="btn-galhos" onClick={() => toggleVariacoes(a)}>
                    {aberto ? '▾ ocultar variações' : `▸ ${info.variations?.length || ''} variações`}
                  </button>
                )}
              </div>

              {/* variações (galhos da árvore) */}
              {aberto && info?.loading && (
                <div className="galho-info">Carregando variações…</div>
              )}
              {aberto && info && !info.loading && info.variations?.map((v) => {
                const ve = varEdits[a.mlb]?.[v.id] || {}
                const precoMudou = ve.price !== undefined && ve.price !== '' && Number(ve.price) !== Number(v.price)
                const qtdMudou = ve.quantity !== undefined && ve.quantity !== '' && Number(ve.quantity) !== Number(v.quantity)
                const skuMudou = ve.sku !== undefined && String(ve.sku) !== String(v.sku || '')
                const mudou = precoMudou || qtdMudou || skuMudou
                const qAtual = Number(ve.quantity !== undefined ? ve.quantity : v.quantity) || 0
                const varAtiva = qAtual > 0
                return (
                  <div key={v.id} className={`galho ${mudou ? 'galho-mudado' : ''}`}>
                    <span className="galho-linha">↳</span>
                    <div className="galho-conteudo">
                      <div className="galho-nome" title={v.combinacao}>{v.combinacao}</div>
                      <div className="galho-campos">
                        <span className={`badge ${varAtiva ? 'b-on' : 'b-off'}`}>
                          {varAtiva ? '🟢' : '🔴'}
                        </span>
                        <label>SKU
                          <input type="text" className="inp-sku"
                            value={ve.sku !== undefined ? ve.sku : (v.sku || '')} disabled={feito}
                            onChange={(ev) => setVarEdit(a.mlb, v.id, 'sku', ev.target.value)} />
                        </label>
                        <label>Preço
                          <input type="number" step="0.01" min="0" className="inp-num"
                            value={ve.price !== undefined ? ve.price : v.price} disabled={feito}
                            onChange={(ev) => setVarEdit(a.mlb, v.id, 'price', ev.target.value)} />
                        </label>
                        <label>Qtd
                          <input type="number" step="1" min="0" className="inp-num"
                            value={ve.quantity !== undefined ? ve.quantity : v.quantity} disabled={feito}
                            onChange={(ev) => setVarEdit(a.mlb, v.id, 'quantity', ev.target.value)} />
                        </label>
                        <button type="button"
                          className={`btn-status ${varAtiva ? 'st-on' : 'st-off'}`}
                          disabled={feito || varBusy === v.id}
                          onClick={() => acaoVariacaoLinha(a, v, varAtiva ? 'pausar' : 'ativar')}>
                          {varBusy === v.id ? '…' : (varAtiva ? 'Pausar' : 'Ativar')}
                        </button>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          )
        })}
      </div>

      {!feito && (
        <button type="button" onClick={confirmar} disabled={!temAlgo || enviando} className="btn-confirmar">
          {enviando ? 'Aplicando…'
            : temAlgo ? `Confirmar (${alteracoesPai.length + totalVarEdits} alteração/ões)`
            : 'Altere algo para confirmar'}
        </button>
      )}
      {feito && <p style={{ margin: '6px 0 0', color: '#16a34a' }}><b>Alterações enviadas.</b></p>}
    </div>
  )
}

// CSS aplicado dentro do Robson (balões, painel, janela)
const cssRespostas = `
/* ---- Botão e janela (responsivo) ---- */
.robson-botao {
  position: fixed; bottom: 24px; right: 24px; width: 60px; height: 60px;
  border-radius: 50%; border: none; background: #2563eb; color: #fff;
  font-size: 26px; cursor: pointer; box-shadow: 0 4px 16px rgba(37,99,235,.4);
  z-index: 99999; display: flex; align-items: center; justify-content: center;
}
.robson-janela {
  position: fixed; bottom: 96px; right: 24px;
  width: 380px; max-width: calc(100vw - 32px);
  height: 540px; max-height: calc(100vh - 130px);
  background: #fff; border-radius: 16px; box-shadow: 0 12px 40px rgba(0,0,0,.28);
  display: flex; flex-direction: column; overflow: hidden; z-index: 99999;
  font-family: system-ui, sans-serif;
}
/* garante que só o corpo role: header/rodapé fixos, meio rolando */
.robson-janela .robson-corpo {
  flex: 1 1 auto; min-height: 0; overflow-y: auto; overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;   /* não deixa a rolagem vazar pra página de trás */
}
.robson-janela .robson-header,
.robson-janela .robson-rodape { flex: 0 0 auto; }

/* chips de sugestão / atalhos */
.robson-chips {
  flex: 0 0 auto; display: flex; gap: 6px; padding: 8px 10px 0; overflow-x: auto;
  scrollbar-width: thin;
}
.robson-chips::-webkit-scrollbar { height: 4px; }
.robson-chips::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 4px; }
.robson-chip {
  flex-shrink: 0; border: 1px solid #dbeafe; background: #eff6ff; color: #1e40af;
  border-radius: 999px; padding: 6px 12px; font-size: 12px; font-weight: 600;
  cursor: pointer; white-space: nowrap; transition: all .12s;
}
.robson-chip:hover { background: #2563eb; color: #fff; border-color: #2563eb; }

/* balões: sem scroll próprio, altura livre (blindado contra CSS global) */
.robson-janela .robson-balao {
  height: auto !important; max-height: none !important; overflow: visible !important;
  box-sizing: border-box;
}
/* celular: ocupa quase a tela toda */
@media (max-width: 480px) {
  .robson-botao { bottom: 16px; right: 16px; }
  .robson-janela {
    right: 12px; left: 12px; bottom: 84px;
    width: auto; max-width: none;
    height: calc(100vh - 100px); max-height: none;
  }
}

/* ---- Modo expandido (modal central) ---- */
.robson-backdrop {
  position: fixed; inset: 0; z-index: 99998;
  background: rgba(15, 23, 42, 0.55);
  animation: robsonFade .15s ease;
}
@keyframes robsonFade { from { opacity: 0 } to { opacity: 1 } }

.robson-janela.robson-expandido {
  top: 50%; left: 50%; right: auto; bottom: auto;
  transform: translate(-50%, -50%);
  width: min(1150px, 97vw);
  height: min(88vh, 1000px);
  max-width: 97vw; max-height: 92vh;
  border-radius: 18px;
}
@media (max-width: 480px) {
  .robson-janela.robson-expandido {
    width: 96vw; height: 88vh; left: 2vw; right: 2vw;
    transform: translateY(-50%);
  }
}
/* blindagem: anula qualquer overflow/altura vinda de CSS global (Bootstrap etc) */
.robson-html {
  max-width: 100%; font-size: 14px; color: #0f172a;
  height: auto !important; max-height: none !important; overflow: visible !important;
}
.robson-html p { margin: 3px 0; line-height: 1.5; }
.robson-html b { color: #1e3a8a; }
.robson-html a { color: #2563eb; text-decoration: underline; word-break: break-all; }
.robson-html .robson-thumb {
  width: 44px; height: 44px; object-fit: cover; border-radius: 6px;
  border: 1px solid #e2e8f0; display: block; background: #f1f5f9;
}
.robson-html ul { margin: 4px 0; padding-left: 18px; }
.robson-html li { margin: 2px 0; }
.robson-html div, .robson-html span {
  height: auto !important; max-height: none !important; overflow: visible !important;
}

/* tabela só para VÁRIOS registros — só ELA rola na horizontal */
.robson-html table {
  border-collapse: collapse; margin: 6px 0; font-size: 12.5px; width: auto;
  display: block; max-width: 100%; overflow-x: auto !important; white-space: nowrap;
}
.robson-html th, .robson-html td {
  border: 1px solid #e2e8f0; padding: 5px 8px; text-align: left; white-space: nowrap;
}
.robson-html thead th { background: #2563eb; color: #fff; font-weight: 600; }
.robson-html tbody tr:nth-child(even) { background: #f8fafc; }

/* ---- Painel de anúncios editável ---- */
.painel-tabela { border-collapse: collapse; width: 100%; font-size: 13px; margin: 8px 0; }
.painel-tabela th, .painel-tabela td {
  border-bottom: 1px solid #eef2f7; padding: 7px 8px; text-align: left;
  vertical-align: middle; white-space: nowrap;
}
/* coluna Produto (3ª) com largura controlada e conteúdo truncado */
.painel-tabela th:nth-child(3),
.painel-tabela td:nth-child(3) {
  width: 280px; max-width: 280px; white-space: normal;
  overflow: hidden; padding-right: 16px;
}
.painel-tabela thead th {
  background: #2563eb; color: #fff; font-weight: 600; font-size: 12px;
  position: sticky; top: 0; z-index: 2; white-space: nowrap;
}
.painel-tabela tbody tr.linha-pai { background: #fff; }
.painel-tabela tr.linha-mudada { background: #fef9c3 !important; }
.painel-tabela tr.linha-pai:hover { background: #f8fafc; }
.painel-tabela tr.linha-pai td { border-top: 1px solid #e2e8f0; }
.painel-tabela tr.linha-filho { background: #f8fafc; }
.painel-tabela tr.linha-filho td { font-size: 12px; border-bottom: 1px dashed #e2e8f0; }
.painel-tabela input[type=checkbox] { width: 16px; height: 16px; cursor: pointer; accent-color: #2563eb; }

.painel-tabela .inp-num {
  width: 80px; box-sizing: border-box; border: 1px solid #cbd5e1; border-radius: 6px;
  padding: 5px 7px; font-size: 12.5px;
}
.painel-tabela .inp-sku {
  width: 90px; box-sizing: border-box; border: 1px solid #cbd5e1; border-radius: 6px;
  padding: 5px 7px; font-size: 12px;
}
.painel-tabela .inp-num:focus,
.painel-tabela .inp-sku:focus { outline: 2px solid #93c5fd; border-color: #2563eb; }

.prod-cell { display: flex; align-items: center; gap: 9px; width: 100%; min-width: 0; }
.prod-info { display: flex; flex-direction: column; min-width: 0; flex: 1; overflow: hidden; gap: 2px; }
.prod-titulo {
  font-size: 12.5px; font-weight: 600; color: #0f172a; line-height: 1.3;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 100%;
}
.prod-mlb {
  font-size: 11px; color: #2563eb; text-decoration: none;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 100%;
}
.prod-mlb:hover { text-decoration: underline; }
.robson-thumb { flex-shrink: 0; }

.painel-tabela .btn-status {
  border: 1px solid transparent; border-radius: 999px; padding: 4px 12px; cursor: pointer;
  font-size: 11.5px; font-weight: 700; white-space: nowrap;
}
.painel-tabela .btn-status.st-on  { background: #dcfce7; color: #166534; border-color: #86efac; }
.painel-tabela .btn-status.st-off { background: #fee2e2; color: #991b1b; border-color: #fca5a5; }
.painel-tabela .btn-exp {
  border: none; background: #e2e8f0; border-radius: 5px; width: 22px; height: 22px;
  cursor: pointer; font-size: 12px; color: #334155;
}
.painel-tabela .btn-exp:hover { background: #cbd5e1; }

/* barra de ações em massa */
.massa-bar {
  display: flex; align-items: center; flex-wrap: wrap; gap: 8px;
  background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 10px;
  padding: 8px 10px; margin: 8px 0; position: sticky; top: 0; z-index: 3;
}
.massa-info { font-size: 12px; font-weight: 700; color: #2563eb; }
.massa-grupo {
  display: flex; align-items: center; gap: 4px;
  padding: 3px 6px; background: #fff; border: 1px solid #e2e8f0; border-radius: 8px;
}
.massa-lbl { font-size: 11px; font-weight: 600; color: #64748b; }
.massa-inp { width: 56px; }
.massa-btn {
  border: 1px solid #cbd5e1; background: #fff; border-radius: 6px; padding: 4px 9px;
  cursor: pointer; font-size: 12px; font-weight: 600; color: #334155;
}
.massa-btn:hover { background: #f1f5f9; }
.massa-btn.st-on  { background: #dcfce7; border-color: #86efac; color: #166534; }
.massa-btn.st-off { background: #fee2e2; border-color: #fca5a5; color: #991b1b; }
.btn-confirmar {
  margin-top: 10px; width: 100%; border: none; border-radius: 10px; padding: 12px;
  background: #2563eb; color: #fff; font-weight: 700; font-size: 14px; cursor: pointer;
  position: sticky; bottom: 0;
}
.btn-confirmar:disabled { background: #94a3b8; cursor: default; }
.painel-aviso {
  background: #fef3c7; border: 1px solid #fde68a; color: #92400e;
  border-radius: 8px; padding: 8px 12px; font-size: 12.5px; margin: 6px 0;
}
.robson-botoes { display: flex; flex-wrap: wrap; gap: 6px; margin: 2px 0 8px; }
.robson-botao-acao {
  border: 1px solid #2563eb; background: #fff; color: #2563eb;
  border-radius: 999px; padding: 6px 14px; font-size: 13px; font-weight: 600;
  cursor: pointer; transition: all .12s;
}
.robson-botao-acao:hover { background: #2563eb; color: #fff; }

/* ---- cards de passo a passo (steps) ---- */
.steps-card {
  align-self: flex-start; max-width: 92%; background: #fff; border: 1px solid #e2e8f0;
  border-radius: 14px; padding: 12px 14px; margin: 2px 0;
  box-shadow: 0 2px 8px rgba(0,0,0,.05);
}
.steps-titulo { font-weight: 700; font-size: 13px; color: #0f172a; margin-bottom: 8px; }
.step-linha { display: flex; align-items: flex-start; gap: 8px; padding: 3px 0; font-size: 12.5px; }
.step-icone { flex-shrink: 0; }
.step-linha.step-wait .step-label { color: #94a3b8; }
.step-linha.step-run  .step-label { color: #2563eb; font-weight: 600; }
.step-linha.step-ok   .step-label { color: #166534; }
.step-linha.step-err  .step-label { color: #991b1b; }
.step-link { margin-left: 8px; color: #2563eb; font-size: 11px; text-decoration: underline; white-space: nowrap; }

/* ---- card de revisão (checkboxes) ---- */
.revisao-card { align-self: flex-start; max-width: 96%; background: #fff; border: 1px solid #e2e8f0; border-radius: 14px; padding: 12px 14px; box-shadow: 0 2px 8px rgba(0,0,0,.06); }
.revisao-titulo { font-weight: 700; font-size: 14px; margin-bottom: 10px; }
.revisao-grupo { margin: 8px 0; border: 1px solid #eef2f7; border-radius: 10px; padding: 8px 10px; }
.revisao-grupo-titulo { font-weight: 700; font-size: 12.5px; color: #334155; margin-bottom: 6px; display: flex; align-items: center; justify-content: space-between; gap: 8px; }
.rev-grupo-btn {
  border: 1px solid #cbd5e1; background: #fff; color: #2563eb; border-radius: 6px;
  padding: 2px 8px; font-size: 11px; font-weight: 600; cursor: pointer;
}
.rev-grupo-btn:hover { background: #eff6ff; }
.revisao-geral { margin-bottom: 8px; }
.rev-todos-btn {
  border: 1px solid #2563eb; background: #eff6ff; color: #1e40af; border-radius: 8px;
  padding: 6px 12px; font-size: 12.5px; font-weight: 700; cursor: pointer; width: 100%;
}
.rev-todos-btn:hover { background: #dbeafe; }
.revisao-item { display: flex; align-items: center; gap: 8px; padding: 4px 0; font-size: 12.5px; cursor: pointer; }
.revisao-item input[type=checkbox] { width: 16px; height: 16px; accent-color: #2563eb; flex-shrink: 0; }
.ri-nome { flex: 1; min-width: 0; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; color: #0f172a; }
.ri-link { color: #2563eb; font-size: 11px; text-decoration: none; white-space: nowrap; }
.ri-link:hover { text-decoration: underline; }
.ri-conta { font-size: 10.5px; color: #64748b; background: #f1f5f9; padding: 1px 6px; border-radius: 999px; white-space: nowrap; }
.ri-tag { font-size: 10.5px; padding: 2px 7px; border-radius: 999px; white-space: nowrap; font-weight: 600; }
.ri-tag.tag-ok { background: #dcfce7; color: #166534; }
.ri-tag.tag-alerta { background: #fee2e2; color: #991b1b; }
.revisao-btn { margin-top: 10px; width: 100%; border: none; border-radius: 10px; padding: 11px; color: #fff; font-weight: 700; font-size: 13.5px; cursor: pointer; }
.revisao-btn:disabled { background: #94a3b8 !important; cursor: default; }

/* ---- cards de anúncio (árvore) ---- */
.cards-lista { display: flex; flex-direction: column; gap: 10px; margin: 8px 0; }
.anuncio-card {
  background: #fff; border: 1px solid #e2e8f0; border-radius: 14px; padding: 12px;
  box-shadow: 0 1px 4px rgba(0,0,0,.04);
}
.anuncio-card.card-mudado { border-color: #fbbf24; background: #fffbeb; }
.card-topo { display: flex; align-items: center; gap: 10px; }
.card-chk { width: 17px; height: 17px; accent-color: #2563eb; cursor: pointer; flex-shrink: 0; }
.card-thumb {
  width: 48px; height: 48px; border-radius: 10px; object-fit: cover;
  border: 1px solid #e2e8f0; background: #f1f5f9; flex-shrink: 0;
}
.card-info { flex: 1; min-width: 0; }
.card-titulo {
  font-size: 13px; font-weight: 700; color: #0f172a; line-height: 1.3;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.card-meta { display: flex; align-items: center; gap: 8px; margin-top: 3px; flex-wrap: wrap; }
.card-mlb { font-size: 11px; color: #2563eb; text-decoration: none; }
.card-mlb:hover { text-decoration: underline; }
.card-conta { font-size: 11px; color: #64748b; background: #f1f5f9; padding: 1px 7px; border-radius: 999px; }
.badge { font-size: 11px; font-weight: 700; padding: 2px 8px; border-radius: 999px; }
.badge.b-on  { background: #dcfce7; color: #166534; }
.badge.b-off { background: #fee2e2; color: #991b1b; }

.card-campos { display: flex; align-items: flex-end; gap: 10px; margin-top: 10px; flex-wrap: wrap; }
.card-campos label, .galho-campos label {
  display: flex; flex-direction: column; font-size: 10.5px; font-weight: 600;
  color: #64748b; gap: 3px;
}
.btn-galhos {
  border: none; background: #eff6ff; color: #2563eb; border-radius: 8px;
  padding: 6px 10px; font-size: 12px; font-weight: 600; cursor: pointer; margin-left: auto;
}
.btn-galhos:hover { background: #dbeafe; }

/* galhos (variações) */
.galho { display: flex; gap: 8px; margin-top: 8px; padding-left: 6px; }
.galho-linha { color: #cbd5e1; font-size: 16px; flex-shrink: 0; }
.galho-conteudo {
  flex: 1; background: #f8fafc; border: 1px solid #eef2f7; border-radius: 10px;
  padding: 8px 10px; border-left: 3px solid #cbd5e1;
}
.galho.galho-mudado .galho-conteudo { border-left-color: #fbbf24; background: #fffbeb; }
.galho-nome { font-size: 12px; font-weight: 600; color: #334155; margin-bottom: 6px; }
.galho-campos { display: flex; align-items: flex-end; gap: 8px; flex-wrap: wrap; }
.galho-info { color: #64748b; font-style: italic; font-size: 12px; padding: 6px 0 0 20px; }

.anuncio-card .inp-num { width: 78px; box-sizing: border-box; border: 1px solid #cbd5e1; border-radius: 6px; padding: 5px 7px; font-size: 12.5px; }
.anuncio-card .inp-sku { width: 92px; box-sizing: border-box; border: 1px solid #cbd5e1; border-radius: 6px; padding: 5px 7px; font-size: 12px; }
.anuncio-card .btn-status {
  border: 1px solid transparent; border-radius: 999px; padding: 6px 14px; cursor: pointer;
  font-size: 12px; font-weight: 700; white-space: nowrap;
}
.anuncio-card .btn-status.st-on  { background: #fee2e2; color: #991b1b; border-color: #fca5a5; }
.anuncio-card .btn-status.st-off { background: #dcfce7; color: #166534; border-color: #86efac; }

/* ---- painel de vendidos ---- */
/* ---- painel de vendidos ---- */
.vend-painel { align-self: flex-start; max-width: 100%; background: #fff; border: 1px solid #e2e8f0; border-radius: 14px; padding: 10px 13px; box-sizing: border-box; }
.vend-abas { display: flex; gap: 4px; flex-wrap: wrap; margin-bottom: 8px; border-bottom: 2px solid #e2e8f0; padding-bottom: 6px; }
.vend-aba {
  border: 1px solid #e2e8f0; background: #f8fafc; color: #475569; border-radius: 8px 8px 0 0;
  padding: 6px 12px; font-size: 12.5px; font-weight: 600; cursor: pointer; display: flex; align-items: center; gap: 6px;
}
.vend-aba.ativa { background: #2563eb; color: #fff; border-color: #2563eb; }
.vend-aba-qtd { background: rgba(0,0,0,.12); border-radius: 999px; padding: 0 6px; font-size: 11px; }
.vend-aba.ativa .vend-aba-qtd { background: rgba(255,255,255,.25); }
.vend-topo-acoes { margin-bottom: 8px; }
.vend-tabela { width: 100%; border-collapse: collapse; font-size: 12.5px; }
.vend-tabela th { background: #f1f5f9; color: #475569; font-weight: 600; text-align: left; padding: 5px 8px; position: sticky; top: 0; }
.vend-tabela td { border-bottom: 1px solid #eef2f7; padding: 5px 8px; }
.vend-desc { max-width: 300px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.vend-abrir { border: 1px solid #2563eb; background: #fff; color: #2563eb; border-radius: 6px; padding: 3px 10px; font-size: 12px; font-weight: 600; cursor: pointer; white-space: nowrap; }
.vend-abrir:hover { background: #2563eb; color: #fff; }
.badge.b-wait { background: #f1f5f9; color: #94a3b8; }
.badge.b-mix { background: #fef9c3; color: #854d0e; }
`

const estilos = {
  header: {
    flexShrink: 0,
    background: '#2563eb', color: '#fff', padding: '12px 16px',
    display: 'flex', alignItems: 'center', gap: 10
  },
  avatar: { fontSize: 26, lineHeight: 1 },
  iconBtn: {
    background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff',
    width: 30, height: 30, borderRadius: 8, cursor: 'pointer', fontSize: 14,
    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
  },
  nome: { fontWeight: 700, fontSize: 15 },
  sub: { fontSize: 11, opacity: 0.85 },
  corpo: {
    flex: 1, minHeight: 0, padding: 12, overflowY: 'auto', display: 'flex',
    flexDirection: 'column', gap: 8, background: '#f8fafc'
  },
  balao: {
    maxWidth: '88%', padding: '9px 13px', borderRadius: 14, fontSize: 14,
    lineHeight: 1.45, wordBreak: 'break-word'
  },
  balaoUser: {
    alignSelf: 'flex-end', background: '#2563eb', color: '#fff',
    borderBottomRightRadius: 3, whiteSpace: 'pre-wrap'
  },
  balaoBot: {
    alignSelf: 'flex-start', maxWidth: '100%', background: '#fff', color: '#0f172a',
    border: '1px solid #e2e8f0', borderBottomLeftRadius: 3,
    boxSizing: 'border-box'
  },
  digitando: { fontStyle: 'italic', opacity: 0.75 },
  rodape: {
    flexShrink: 0,
    display: 'flex', gap: 8, padding: 10, borderTop: '1px solid #e2e8f0', background: '#fff'
  },
  input: {
    flex: 1, border: '1px solid #cbd5e1', borderRadius: 10, padding: '9px 12px',
    fontSize: 14, outline: 'none'
  },
  enviar: {
    border: 'none', background: '#2563eb', color: '#fff', borderRadius: 10,
    padding: '0 16px', cursor: 'pointer', fontSize: 16
  }
}